import { useFlowchartCoreBase } from './useFlowchartCoreBase';
import { useFlowchartCoreGraphAndTidy } from './useFlowchartCoreGraphAndTidy';
import { useFlowchartCoreFocus } from './useFlowchartCoreFocus';
import { useFlowchartCoreRefsAndGraphKeys } from './useFlowchartCoreRefsAndGraphKeys';
import { useFlowchartLayoutSurfaceEpoch } from './useFlowchartLayoutSurfaceEpoch';
import { useFlowchartCoreAuxiliaryPipeline } from './useFlowchartCoreAuxiliaryPipeline';
import { gatherFlowchartCorePublicApi } from './gatherFlowchartCorePublicApi';

/**
 * Composes flowchart layout, interaction, graph, focus, viewport, and actions for FlowchartView.
 */
export function useFlowchartCore({
  tasks,
  statuses,
  nestedSprints,
  allTags = [],
  customFields = [],
  autoArrange,
  campaignId,
  isMultiCampaignMode,
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
  onDependencyChange,
  onConditionsChange,
  onPositionChange,
  onPositionChangeBatch,
  wrapLastUndoAction,
  onApplyTaskPositions,
  onDeleteTasks,
  onFocusModeChange,
  onNestTasks,
  onPasteTasks,
  onCreateConnectedTask,
  onInsertNodeOnEdge,
  onInsertMilestoneOnEdge,
  onNestCampaign,
  onAddTask,
  initialFocusedTaskIds = null,
  chainOrder = null,
  onAddCampaignToView,
  multiCampaignCampaigns = [],
  openCampaignPickerForPaste,
  hasActiveFilters = false,
  onChainTouched = null,
  localSprint = null,
  allSprints = [],
  loadFullSprint,
  activeView = null,
  currentView = null,
}) {
  const base = useFlowchartCoreBase({
    tasks,
    autoArrange,
  campaignId,
  isMultiCampaignMode,
  chainOrder,
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
  onDependencyChange,
  onConditionsChange,
  onPositionChange,
  onPositionChangeBatch,
  onDeleteTasks,
  hasActiveFilters,
  onChainTouched,
  campaignName: localSprint?.name || '',
  });

  const interaction = base.interaction;

  const refs = useFlowchartCoreRefsAndGraphKeys({
    tasks,
    autoPositions: base.autoPositions,
    chainOrder,
    base,
  });

  const layoutSurfaceEpoch = useFlowchartLayoutSurfaceEpoch({ activeView, currentView });

  const graphAndTidy = useFlowchartCoreGraphAndTidy({
    campaignContext: {
      tasks,
      nestedSprints,
      statuses,
      localSprint,
      allSprints,
      isMultiCampaignMode,
      onPositionChangeBatch,
      hasActiveFilters,
      onPositionChange,
      campaignId,
      wrapLastUndoAction,
      onApplyTaskPositions,
    },
    base,
    refs,
  });

  const { graph, handleAutoLayout, handleAutoLayoutInstant, isAnimatingTidy, isPartialTidyToolbarLoading } = graphAndTidy;

  const focusCore = useFlowchartCoreFocus({
    tasks,
    nodePositions: graph.nodePositions,
    nodeDimensions: base.nodeDimensions,
    edges: graph.edges,
    containerRef: interaction.containerRef,
    pan: interaction.pan,
    zoom: interaction.zoom,
    setPanUnconstrained: interaction.setPanUnconstrained,
    setZoom: interaction.setZoom,
    onFocusModeChange,
    disableDragRef: interaction.disableDragRef,
    isFocusModeRef: interaction.isFocusModeRef,
    setSelectedNodes: interaction.setSelectedNodes,
    selectedNodes: interaction.selectedNodes,
    isMultiCampaignMode,
    statuses,
    initialFocusedTaskIds,
    effectivePositionsRef: base.effectivePositionsRef,
    handleConnectionComplete: base.handleConnectionComplete,
  });

  const auxiliary = useFlowchartCoreAuxiliaryPipeline({
    immediateNestAction: interaction.immediateNestAction,
    onNestTasks,
    clearImmediateNestAction: interaction.clearImmediateNestAction,
    focusCore,
    interaction,
    isAnimatingLayout: base.isAnimatingLayout,
    nodeDimensions: base.nodeDimensions,
    tasks,
    nodePositions: graph.nodePositions,
    customPositionsRef: interaction.customPositionsRef,
    campaignId,
    autoPositions: base.autoPositions,
    setPanUnconstrained: interaction.setPanUnconstrained,
    setZoom: interaction.setZoom,
    handleGoToStart: interaction.handleGoToStart,
    skipResizeAdjustment: interaction.skipResizeAdjustment,
    onCreateConnectedTask,
    onInsertNodeOnEdge,
    onInsertMilestoneOnEdge,
    handleAutoLayout,
    followTaskAfterLayout: base.followTaskAfterLayout,
    nestedSprints,
    allTags,
    customFields,
    onPasteTasks,
    onDeleteTasks,
    handleAfterDeleteStable: base.handleAfterDeleteStable,
    isMultiCampaignMode,
    openCampaignPickerForPaste,
    loadFullSprint,
    allSprints,
    onAddTask,
    setSelectedNodes: interaction.setSelectedNodes,
    setPanAnimated: interaction.setPanAnimated,
    onNestCampaign,
    onAddCampaignToView,
    multiCampaignCampaigns,
    setCanvasContextMenuState: base.setCanvasContextMenuState,
    canvasMenuRef: base.canvasMenuRef,
  });

  return gatherFlowchartCorePublicApi({
    base,
    interaction,
    graph,
    focusCore,
    auxiliary,
    tidy: { handleAutoLayout, handleAutoLayoutInstant, isAnimatingTidy, isPartialTidyToolbarLoading },
    layoutSurfaceEpoch,
  });
}

export default useFlowchartCore;
